import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useNavigationType } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useOptions } from "@/hooks/useOptions";
import { useResourceList } from "@/hooks/useResource";
import { PageHeader } from "@/components/kit/PageHeader";
import { DataTable } from "@/components/kit/DataTable";
import { StatisticsFilters } from "@/components/statistics/StatisticsFilters";
import { fmtMoney, fmtNumber, getDisplayTimezone, isoDayInTz } from "@/lib/format";
import { cn } from "@/lib/utils";

const TABS = [["day", "Days"], ["stream", "Streams"], ["user", "Users"], ["offer", "Offers"], ["advertiser", "Advertisers"], ["sub_id", "Sub IDs"]];
const SUB_KEYS = Array.from({ length: 9 }, (_, i) => `sub_id_${i + 1}`);
const ALL = "all";
const STATE_KEY = "affilix_stats_state_v2";
const COUNT_COLORS = { lead: "#38BDF8", sale: "#22C55E", redep: "#2DD4BF", revshare: "#F59E0B", trash: "#EF4444" };

function last7() {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - 6);
  return { date_from: isoDayInTz(from), date_to: isoDayInTz(to) };
}

const cleanParams = (f) => {
  const out = {};
  Object.entries(f).forEach(([k, v]) => {
    if (Array.isArray(v)) { if (v.length) out[k] = v.join(","); }
    else if (v && v !== ALL) out[k] = v;
  });
  return out;
};

const numCell = (key) => (r) => <span className={cn("tabular-nums", r.__total ? "font-semibold text-foreground" : "text-foreground/80")}>{fmtNumber(r[key])}</span>;
const moneyCell = (key) => (r) => (
  <span className={cn("tabular-nums text-foreground/90", r.__total && "font-semibold text-foreground")}>{fmtMoney(r[key])}</span>
);

export default function Statistics() {
  const { user } = useAuth();
  const opts = useOptions();
  const navigate = useNavigate();
  const navType = useNavigationType();
  const staff = ["manager", "tenant_admin", "super_admin"].includes(user.role);
  const { data: offersData } = useResourceList("offers", {});
  const { data: streamsData } = useResourceList("streams", {});

  const tabsToShow = staff ? TABS : TABS.filter(([k]) => !["user", "advertiser"].includes(k));

  // Restore prior Statistics state ONLY on browser Back (POP). Fresh entry via menu = defaults.
  const saved = useMemo(() => {
    if (navType !== "POP") return null;
    try { return JSON.parse(sessionStorage.getItem(STATE_KEY) || "null"); } catch { return null; }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const init = useMemo(() => ({ ...last7() }), []);
  const [tab, setTab] = useState(saved?.tab || "day");
  const [subField, setSubField] = useState(saved?.subField || "sub_id_1");
  const [subSearch, setSubSearch] = useState(saved?.subSearch || "");
  const [draft, setDraft] = useState(saved?.draft || init);
  const [applied, setApplied] = useState(saved?.applied || init);

  // Buyers must never land on a hidden tab (e.g. via a stale restored state).
  useEffect(() => {
    if (!staff && ["user", "advertiser"].includes(tab)) setTab("day");
  }, [staff, tab]);

  const set = (k, v) => setDraft((s) => ({ ...s, [k]: v }));
  const apply = () => setApplied(draft);
  const reset = () => { setDraft(init); setApplied(init); };
  const dirty = JSON.stringify(draft) !== JSON.stringify(applied);

  const params = useMemo(() => ({ ...cleanParams(applied), group_by: tab, sub_field: subField, tz: getDisplayTimezone() }), [applied, tab, subField]);
  const { data, isLoading } = useQuery({
    queryKey: ["stats-report", params],
    queryFn: async () => (await http.get("/statistics/report", { params })).data,
    placeholderData: (prev) => prev,
    refetchOnMount: "always",
  });

  // Persist current state continuously so Back can restore it.
  useEffect(() => {
    sessionStorage.setItem(STATE_KEY, JSON.stringify({ tab, subField, subSearch, draft, applied, scrollY: window.scrollY }));
  }, [tab, subField, subSearch, draft, applied]);

  // Restore scroll position on Back — only once the table data has resolved (so the document is
  // tall enough; restoring too early clamps scrollY to 0).
  const restoredScroll = useRef(false);
  useEffect(() => {
    if (saved && !restoredScroll.current && typeof saved.scrollY === "number" && data) {
      restoredScroll.current = true;
      requestAnimationFrame(() => window.scrollTo(0, saved.scrollY));
    }
  }, [saved, data]);

  const rows = data?.rows || [];
  const totals = data?.totals;
  // Sub IDs tab only: quick client-side search by the sub value; totals follow the visible rows.
  const subQ = tab === "sub_id" ? subSearch.trim().toLowerCase() : "";
  const viewRows = subQ ? rows.filter((r) => String(r.label || r.key || "").toLowerCase().includes(subQ)) : rows;
  const viewTotals = useMemo(() => {
    if (!subQ) return totals;
    if (!viewRows.length) return null;
    const keys = ["clicks", "unique_clicks", "lead", "sale", "redep", "revshare", "trash", "payout", "payout_hold",
      "revenue", "revenue_approved", "revenue_unpaid", "profit", "profit_approved"];
    const t = {};
    keys.forEach((k) => { t[k] = viewRows.reduce((s, r) => s + (Number(r[k]) || 0), 0); });
    return t;
  }, [subQ, viewRows, totals]); // eslint-disable-line react-hooks/exhaustive-deps
  const dimLabel = { day: "Date", stream: "Stream", user: "User", offer: "Offer", advertiser: "Advertiser", sub_id: subField }[tab];

  // Deep-link to Conversions carrying the full statistics context + the clicked dimension/metric.
  const goConversions = (row, metric) => {
    sessionStorage.setItem(STATE_KEY, JSON.stringify({ tab, subField, draft, applied, scrollY: window.scrollY }));
    const p = new URLSearchParams();
    const a = applied;
    const j = (v) => (Array.isArray(v) ? v.join(",") : (v && v !== ALL ? v : ""));
    if (j(a.stream_id)) p.set("stream_id", j(a.stream_id));
    if (j(a.offer_id)) p.set("offer_id", j(a.offer_id));
    if (j(a.advertiser_id)) p.set("advertiser_id", j(a.advertiser_id));
    if (j(a.buyer_id)) p.set("buyer_id", j(a.buyer_id));
    if (j(a.country)) p.set("country", j(a.country));
    if (j(a.payment_status)) p.set("payment_status", j(a.payment_status));
    let df = a.date_from, dt = a.date_to;
    if (tab === "day") { df = row.key; dt = row.key; }
    else if (tab === "stream") p.set("stream_id", row.key);
    else if (tab === "user") p.set("buyer_id", row.key);
    else if (tab === "offer") p.set("offer_id", row.key);
    else if (tab === "advertiser") p.set("advertiser_id", row.key);
    else if (tab === "sub_id") { p.set("sub_field", subField); p.set("sub_value", row.key); }
    if (df) p.set("date_from", df);
    if (dt) p.set("date_to", dt);
    p.set("tz", getDisplayTimezone());
    // Match the exact rows behind each counter: Lead/Sale = live (Hold+Approved, never Trash);
    // Trash = the Trash bucket regardless of conversion type.
    if (metric === "lead") { p.set("status", "lead"); p.set("exclude_trash", "1"); }
    else if (metric === "sale") { p.set("status", "sale"); p.set("exclude_trash", "1"); }
    else if (metric === "redep") { p.set("status", "redep"); p.set("exclude_trash", "1"); }
    else if (metric === "revshare") { p.set("status", "revshare"); p.set("exclude_trash", "1"); }
    else if (metric === "trash") { p.set("payment_status", "trash"); }
    navigate(`/conversions?${p.toString()}`);
  };

  const countCell = (key) => (r) => {
    const color = COUNT_COLORS[key];
    if (r.__total) return <span className="font-semibold tabular-nums" style={{ color }}>{fmtNumber(r[key])}</span>;
    if (!r[key]) return <span className="tabular-nums text-muted-foreground/40">0</span>;
    return (
      <button onClick={() => goConversions(r, key)} data-testid={`stats-link-${key}-${r.key}`}
        className="tabular-nums font-medium underline-offset-2 hover:underline" style={{ color }}>{fmtNumber(r[key])}</button>
    );
  };

  const dimCol = {
    key: "label", label: dimLabel, headerClassName: "min-w-[160px]",
    render: (r) => r.__total
      ? <span className="font-semibold text-foreground">Total</span>
      : (
        <div>
          <span className="font-medium text-foreground">{r.label || "—"}</span>
          {r.sublabel ? <span className="block text-xs text-muted-foreground">{r.sublabel}</span> : null}
        </div>
      ),
  };

  const divider = "border-l border-white/[0.12]";
  const columns = [
    dimCol,
    { key: "clicks", label: "Clicks", align: "right", render: numCell("clicks") },
    { key: "unique_clicks", label: "Unique", align: "right", render: numCell("unique_clicks") },
    { key: "lead", label: "Lead", align: "right", render: countCell("lead") },
    { key: "sale", label: "Sale", align: "right", render: countCell("sale") },
    { key: "redep", label: "REDEP", align: "right", render: countCell("redep") },
    { key: "revshare", label: "RS", align: "right", render: countCell("revshare") },
    { key: "trash", label: "Trash", align: "right", render: countCell("trash") },
    { key: "payout", label: "Payout", align: "right", className: divider, headerClassName: divider, render: moneyCell("payout") },
    { key: "payout_hold", label: "Payout Hold", align: "right", render: moneyCell("payout_hold") },
    // Revenue & Profit are internal network economics — hidden from Buyers.
    ...(staff ? [
      { key: "revenue", label: "Revenue", align: "right", render: moneyCell("revenue") },
      { key: "revenue_approved", label: "Revenue Approved", align: "right", render: moneyCell("revenue_approved") },
      { key: "revenue_unpaid", label: "Revenue Unpaid", align: "right", render: moneyCell("revenue_unpaid") },
      { key: "profit", label: "Profit", align: "right", render: moneyCell("profit") },
      { key: "profit_approved", label: "Profit Approved", align: "right", render: moneyCell("profit_approved") },
    ] : []),
  ];

  const tableRows = viewTotals && viewRows.length > 0 ? [...viewRows, { ...viewTotals, id: "__total", __total: true, label: "Total" }] : viewRows;

  return (
    <div className="space-y-4">
      <PageHeader title="Statistics" subtitle="Multi-dimensional performance analytics — conversions on the left, live financials on the right." />

      <div className="inline-flex flex-wrap gap-0.5 rounded-lg border border-white/[0.06] bg-[#0E0E10] p-1" data-testid="stats-tabs">
        {tabsToShow.map(([key, label]) => (
          <button key={key} data-testid={`stats-tab-${key}`} onClick={() => setTab(key)}
            className={cn("relative rounded-md px-3.5 py-1.5 text-[13px] font-medium transition-colors",
              tab === key ? "bg-white/10 text-white" : "text-zinc-400 hover:text-foreground")}>
            {label}
          </button>
        ))}
      </div>

      <StatisticsFilters draft={draft} set={set} apply={apply} reset={reset} dirty={dirty} staff={staff}
        showManager={["tenant_admin", "super_admin"].includes(user.role)
          || (user.permissions || []).includes("*") || (user.permissions || []).includes("statistics.view.tenant")}
        offers={offersData?.items || []} streams={streamsData?.items || []} opts={opts} />

      {tab === "sub_id" && (
        <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="stats-groupby-sub">
          <span className="text-muted-foreground">Group rows by</span>
          <select value={subField} onChange={(e) => setSubField(e.target.value)} data-testid="stats-groupby-sub-select"
            className="h-9 rounded-md border border-white/[0.1] bg-[#0A0A0A] px-2 text-sm text-foreground outline-none focus:border-white/25">
            {SUB_KEYS.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <input value={subSearch} onChange={(e) => setSubSearch(e.target.value)} data-testid="stats-sub-search"
            placeholder="Search Sub ID value…"
            className="h-9 w-[220px] rounded-md border border-white/[0.1] bg-[#0A0A0A] px-3 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-white/25" />
        </div>
      )}

      <DataTable testId="statistics-table" columns={columns} rows={tableRows} loading={isLoading}
        emptyText="No data for the selected filters." />
    </div>
  );
}
